import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, catchError, mergeMap, of, switchMap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';

@Injectable({
  providedIn: 'root'
})
export class MorbidityMortalityService {
  private apiUrl = 'api';
  private morbidityMortalitySubject = new BehaviorSubject<any[]>([]);

  morbidityMortality$ = this.morbidityMortalitySubject.asObservable();

  groupId: any;
  currentPage = 1;
  pageSize = 500;
  isLoading = false;

  constructor(private http: HttpClient, private groupService: GroupService, private socketService: SocketService) {

    this.setupRealTimeUpdates();

    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.currentPage = 1;
          this.morbidityMortalitySubject.next([]);
          return this.loadMorbidityMortality();
        }
        return EMPTY;
      })
    ).subscribe();
  }

  private setupRealTimeUpdates(): void {
    // Listen for a new item being added
    this.socketService.listen('noteAdded', 'morbidity-mortality-service').subscribe((newData) => {
      const current = this.morbidityMortalitySubject.getValue();
      if (newData.isMorbidity || newData.isMortality) {
        this.morbidityMortalitySubject.next([...current, newData]); // Add the new item
      }
    });

    // Listen for updates
    this.socketService.listen('noteUpdated', 'morbidity-mortality-service').subscribe((updated) => {
      // Update in the main list
      const current = this.morbidityMortalitySubject.getValue();
      const index = current.findIndex(p => p.id === updated.id);
      if (index !== -1) {
        current[index] = updated;
        this.morbidityMortalitySubject.next([...current]); // Replace the item in the array
      }
    });

    // Listen for an item being deleted
    this.socketService.listen('noteDeleted', 'morbidity-mortality-service').subscribe((deleted) => {
      const current = this.morbidityMortalitySubject.getValue();
      const updated = current.filter(p => p.id !== deleted.id);
      this.morbidityMortalitySubject.next(updated); // Remove the deleted item
    });

    this.socketService.listen('patientUpdated', 'morbidity-mortality-service').subscribe((updatedPatient) => {
      // Get the current value of morbidityMortalitySubject
      const current = this.morbidityMortalitySubject.getValue();

      // Map through the current morbidity mortality list to update the specific patient's details
      const updatedMorbidityMortality = current.map(item => {
        if (item.patientId === updatedPatient.id) {
          // Update the diagnosis for the specific patient
          return {
            ...item,
            Patient: {
              ...item.Patient,
              name: updatedPatient.name,
              surname: updatedPatient.surname,
              diagnosis: updatedPatient.diagnosis,
              patientNumber: updatedPatient.patientNumber,
              requiresValidation: updatedPatient.requiresValidation,
              requiresICDValidation: updatedPatient.requiresICDValidation,
              wasSelfBoarded: updatedPatient.wasSelfBoarded,
              status: updatedPatient.status,
            }
          };
        }
        return item;
      });

      // Update the morbidityMortalitySubject with the new list
      this.morbidityMortalitySubject.next(updatedMorbidityMortality);
    });


  }

  loadMorbidityMortality(): Observable<any[]> {
    if (this.isLoading) {
      return EMPTY;
    }
    this.isLoading = true;
    return this.getMorbidityMortalityByGroup(this.groupId, this.currentPage, this.pageSize).pipe(
      mergeMap((res: any) => {
        const items = res.data;
        const current = this.morbidityMortalitySubject.getValue();
        const updated = [...current, ...items];
        this.morbidityMortalitySubject.next(updated);
        if (items.length === this.pageSize) {
          this.currentPage++;
          this.isLoading = false;
          return this.loadMorbidityMortality();
        } else {
          this.isLoading = false;
          return of(updated);
        }
      }),
      catchError(error => {
        this.isLoading = false;
        return of([]);
      })
    );
  }

  getMorbidityMortalityByGroup(groupId: string, page: number, pageSize: number): Observable<any[]> {
    const token = localStorage.getItem('token');
    return this.http.get<any[]>(`${this.apiUrl}/morbidity-mortality/group/${groupId}?page=${page}&pageSize=${pageSize}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


}
